import { useTagPlan } from 'features/tagPlan/business/useTagPlan';
import isEmpty from 'lodash/isEmpty';
import { RegistrationProcess, Resend } from 'pleinchamp-api-client';
import React, { ComponentPropsWithRef, FC, PropsWithRef, useEffect, useRef, useState } from 'react';
import { ReCAPTCHAProps } from 'react-google-recaptcha';
import { Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RegistrationApi } from '@api/business/api.utils';
import { SignUpProfile } from '@auth/business/auth.utils';
import { BasicLink } from '@components/buttons/BasicLink/BasicLink.component';
import { PrimaryButton } from '@components/buttons/FlatButton/FlatButton.component';
import { PlcFlatRectLoader } from '@components/Loaders/PlcFlatRectLoader.component';
import { PlcMessage } from '@components/PlcMessage/PlcMessage.component';
import { Text } from '@components/Text/Text.component';
import { formErrorsMsgs, formRegex } from '@form/business/form.types';
import { FormValidators, useFormState } from '@form/business/useFormState.hook';
import { Checkbox } from '@form/components/Checkbox/Checkbox.component';
import { FormHints } from '@form/components/FormHints/FormHints.component';
import { Input } from '@form/components/Input/Input.component';
import { PasswordInput } from '@form/components/PasswordInput/PasswordInput.component';
import { PlcForm } from '@form/components/PlcForm/PlcForm.component';
import { useTranslation } from '@locales/useTranslation.hook';
import { PlcDispatch, RootState } from '@store/store';
import { useClassNames } from '@styles/breakpoints';
import { PlcColor } from '@styles/colors';
import { useDebounce } from '@utils/debounce.hook';
import { urlEnum } from '@utils/url';
import classnames from 'classnames';
import { ATLevel2 } from 'features/tagPlan/business/TagPlan.types';
import dynamic from 'next/dynamic';

import './SignUpForm.scss';

const captchaToken = process.env.NEXT_STATIC_RECAPTCHA_TOKEN;

const ReCAPTCHA = dynamic<PropsWithRef<ReCAPTCHAProps>>(() => import('react-google-recaptcha'), {
  loading: () => <PlcFlatRectLoader fixedWidth height={75} uniqueKey="reCAPTCHA" width={300} />,
  ssr: false,
});

interface Props extends ComponentPropsWithRef<'form'> {
  profile: SignUpProfile;
  disabled?: boolean;
  active: boolean;
  toggleIsEditing?: (formId: SignUpProfile, isEditing: boolean) => void;
  onSignUpComplete: (resend: Resend) => void;
  withWrapper?: boolean;
  registrationProcess: RegistrationProcess;
  teamInviteToken?: string;
  defaultEmail?: string;
}

type SignUpFormState = {
  email: string;
  password: string;
  cgu: boolean;
  newsletter: boolean;
  partnerOffers: boolean;
  commercialOffers: boolean;
  captcha: boolean;
};

const initialFormState: SignUpFormState = {
  captcha: false,
  cgu: false,
  commercialOffers: false,
  email: '',
  newsletter: false,
  partnerOffers: false,
  password: '',
};

const SignUpForm: FC<Props> = ({
  profile,
  className,
  active,
  disabled,
  toggleIsEditing,
  onSignUpComplete,
  registrationProcess,
  withWrapper = true,
  teamInviteToken,
  defaultEmail,
  ...rest
}) => {
  const { t } = useTranslation(['auth', 'common']);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const cguRef = useRef<HTMLLabelElement>(null);
  const captchaContainerRef = useRef<HTMLDivElement>(null);
  const captchaRef = useRef(null);
  const [registrationProcessState, setRegistrationProcessState] = useState<RegistrationProcess>(registrationProcess);

  const {
    user: { signUp },
    auth: { signIn },
  } = useDispatch<PlcDispatch>();
  const { errorCode } = useSelector((state: RootState) => state.user);

  /* eslint-disable sort-keys-fix/sort-keys-fix */
  const validators: FormValidators<SignUpFormState> = {
    email: {
      method: value => [
        {
          isValid: !isEmpty(value),
          message: t(formErrorsMsgs.emptyEmail),
        },
        {
          isValid: isEmpty(value) || formRegex.email.test(value),
          message: t(formErrorsMsgs.fieldNotValid),
        },
      ],
      ref: emailRef,
    },
    password: {
      method: value => [
        {
          isValid: !isEmpty(value),
          message: t(formErrorsMsgs.emptyPassword),
        },
        {
          isValid: isEmpty(value) || formRegex.password.test(value),
          message: t(formErrorsMsgs.fieldNotValid),
        },
      ],
      ref: passwordRef,
    },
    cgu: {
      method: value => [
        {
          isValid: value,
          message: t(formErrorsMsgs.needToCheck),
        },
      ],
      ref: cguRef,
    },
    captcha: {
      method: value => [
        {
          isValid: value,
          message: t(formErrorsMsgs.fieldEmpty),
        },
      ],
      ref: captchaContainerRef,
    },
  };

  const {
    formData,
    setFormField,
    isSubmitted,
    isDirty,
    disabledSubmitButton,
    plcFormProps,
    setErrorMessage,
    Message,
  } = useFormState({
    initialFormState: defaultEmail ? { ...initialFormState, email: defaultEmail } : initialFormState,
    useNavigationWarn: true,
    validators,
  });

  const { email, password, cgu, newsletter, commercialOffers, partnerOffers, captcha } = formData;

  useEffect(() => {
    if (toggleIsEditing) toggleIsEditing(profile, isDirty);
  }, [profile, isDirty]);

  useEffect(() => (active ? emailRef?.current?.focus() : undefined), []);

  const { tagClick } = useTagPlan();

  function onSubmitCallback() {
    return signUp({
      email,
      isFreeTOSAgreed: profile === SignUpProfile.member ? cgu : undefined,
      isNewsletterAgreed: newsletter,
      isPLCOffersAgreed: commercialOffers,
      isPartnersOffersAgreed: partnerOffers,
      isProTOSAgreed: profile === SignUpProfile.pro ? cgu : undefined,
      password,
      registrationProcess: registrationProcessState,
      teamInviteToken,
    })
      .then(() => {
        const tagPageNameI18nArray =
          // eslint-disable-next-line no-nested-ternary
          profile === SignUpProfile.member
            ? ['auth.sign-up.tag.title', 'auth.sign-up.tag.member']
            : registrationProcessState === RegistrationProcess.Cac
            ? ['auth.pro.tag.title', 'auth.sign-up.tag.cac']
            : undefined;
        if (tagPageNameI18nArray) {
          tagClick({
            level2: ATLevel2.SignUp,
            tagPageNameI18nArray,
          });
        }
        if (registrationProcessState !== RegistrationProcess.Invitation) {
          return undefined;
        }
        return signIn({ email, password, callbackUrl: urlEnum.welcome });
      })
      .then(() => {
        return onSignUpComplete({ email, registrationProcess: registrationProcessState });
      })
      .catch(e => {
        const errorMessage = e?.response?.data?.message;
        if (errorMessage) {
          setErrorMessage(errorMessage);
        }
        document?.getElementById('sign-up-form-section')?.scrollIntoView();
      });
  }

  const emailDebounce = useDebounce(email, 1500);
  useEffect(() => {
    if (registrationProcess === RegistrationProcess.RegularCac && email) {
      RegistrationApi.isEligiblePro(email).then(res => {
        setRegistrationProcessState(res.data?.isEligiblePro ? RegistrationProcess.Cac : RegistrationProcess.Regular);
      });
    }
  }, [emailDebounce]);

  const rootClass = classnames('sign-up-form', withWrapper && 'with-wrapper', profile, className);
  const contentClass = useClassNames({
    all: ['sign-up-form-content', 'plc-pt-l', 'plc-pb-basis', 'plc-px-m'],
    m: ['plc-px-l'],
  });
  const emailPasswordWrapper = useClassNames({
    all: withWrapper ? 'plc-mb-basis' : 'plc-mb-l',
    m: withWrapper ? '' : 'flex',
  });
  const emailClass = useClassNames({
    all: ['plc-mb-m'],
    m: withWrapper ? '' : ['plc-mb-0', 'plc-mr-l', 'flex-1'],
  });
  const passwordClass = useClassNames({
    m: withWrapper ? '' : 'flex-1',
  });

  const isLight = profile === SignUpProfile.pro && withWrapper;
  const textFlavour = isLight ? 'light' : 'dark';
  const checkboxVariant = withWrapper ? 'tiny' : 'small';

  return (
    <PlcForm autoComplete="off" className={rootClass} onSubmitCallback={onSubmitCallback} {...plcFormProps} {...rest}>
      <div className={withWrapper ? contentClass : undefined}>
        {withWrapper && (
          <Text className="sign-up-form-description" flavour={textFlavour} tag="p" variant="h5">
            <Trans i18nKey="auth.sign-up.description" ns="auth">
              First line
              <br />
              <br />
            </Trans>
          </Text>
        )}
        {errorCode &&
          active &&
          (<Message className="sign-up-form-description" /> || (
            <PlcMessage className="sign-up-form-description" errorCode={errorCode} />
          ))}
        <div className={emailPasswordWrapper}>
          <Input
            ref={emailRef}
            autoComplete="new-email"
            className={emailClass}
            disabled={disabled || !!defaultEmail}
            id={`sign-up-${profile}-email-input`}
            isHintVisible={isSubmitted}
            labelFlavour={isLight ? 'light' : 'grey'}
            labelI18n={isLight ? 'auth.pro.email.label' : 'auth.email.label'}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFormField('email', event.target.value)}
            placeholder={t('auth.email.placeholder')}
            style={{ color: isLight ? PlcColor.WHITE : PlcColor.BLUE_LAND }}
            type="email"
            validators={validators.email?.method(email)}
            value={email}
          />
          <PasswordInput
            ref={passwordRef}
            autoComplete="new-password"
            className={passwordClass}
            disabled={disabled}
            hintFlavour={isLight ? 'light' : 'dark'}
            hintI18n="auth.password.hint"
            isHintVisible={isSubmitted}
            labelFlavour={isLight ? 'light' : 'grey'}
            labelI18n="auth.password.label"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFormField('password', event.target.value)}
            placeholder={t('auth.password.placeholder')}
            style={{ color: isLight ? PlcColor.WHITE : PlcColor.BLUE_LAND }}
            validators={validators.password?.method(password)}
            value={password}
          />
        </div>
        <Checkbox
          ref={cguRef}
          checked={cgu}
          className="sign-up-checkbox"
          disabled={disabled}
          isHintVisible={isSubmitted}
          label={
            <Text flavour={textFlavour} tag="div" variant={checkboxVariant}>
              <Trans i18nKey={`auth.sign-up.${profile}.cgu`} ns="auth">
                Yes
                <BasicLink
                  href={urlEnum.tos}
                  isUnderlined
                  target="_blank"
                  textProps={{ flavour: textFlavour, variant: checkboxVariant }}
                >
                  cgu link
                </BasicLink>
                and
                <BasicLink
                  href={urlEnum.privacyPolicy}
                  isUnderlined
                  target="_blank"
                  textProps={{ flavour: textFlavour, variant: checkboxVariant }}
                >
                  gdpr
                </BasicLink>
              </Trans>
            </Text>
          }
          multilineLabel
          onCheck={checked => setFormField('cgu', checked)}
          validators={validators.cgu?.method(cgu)}
        />
        <div className="separator" />
        <Checkbox
          checked={newsletter}
          className="sign-up-checkbox"
          disabled={disabled}
          label={<Text flavour={textFlavour} i18nKey="auth.sign-up.newsletter" tag="div" variant={checkboxVariant} />}
          multilineLabel
          onCheck={checked => setFormField('newsletter', checked)}
        />
        <Checkbox
          checked={commercialOffers}
          className="sign-up-checkbox"
          disabled={disabled}
          label={
            <Text flavour={textFlavour} i18nKey="auth.sign-up.commercial-offers" tag="div" variant={checkboxVariant} />
          }
          multilineLabel
          onCheck={checked => setFormField('commercialOffers', checked)}
        />
        <Checkbox
          checked={partnerOffers}
          className="sign-up-checkbox"
          disabled={disabled}
          label={
            <Text flavour={textFlavour} tag="div" variant={checkboxVariant}>
              <Trans i18nKey="auth.sign-up.partners-commercial-offers" ns="auth">
                Yes
                <BasicLink
                  href={urlEnum.partners}
                  isUnderlined
                  target="_blank"
                  textProps={{ flavour: textFlavour, variant: checkboxVariant }}
                >
                  partners
                </BasicLink>
              </Trans>
            </Text>
          }
          multilineLabel
          onCheck={checked => setFormField('partnerOffers', checked)}
        />
        <div ref={captchaContainerRef} className="plc-mb-basis">
          {captchaToken && (
            <ReCAPTCHA
              // @ts-ignore
              ref={captchaRef}
              onChange={(token: string | null) => setFormField('captcha', token !== null)}
              sitekey={captchaToken}
            />
          )}
          <FormHints isHintVisible={isSubmitted} validators={validators.captcha?.method(captcha)} />
        </div>
        <PrimaryButton
          disabled={disabled || disabledSubmitButton}
          isPro={profile === SignUpProfile.pro && !withWrapper}
          isSmall={withWrapper}
          textProps={{
            i18nKey:
              registrationProcessState === RegistrationProcess.Invitation
                ? 'auth.sign-up.affiliate.button'
                : `auth.sign-up.${profile}.button`,
          }}
          type="submit"
        />
        <Text
          className="sign-up-form-disclaimer"
          flavour="grey"
          i18nKey="auth.sign-up.pro.disclaimer"
          tag="div"
          variant="tiny"
        />
      </div>
    </PlcForm>
  );
};

export { SignUpForm };
